import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FaStar,
  FaBolt,
  FaCrown,
  FaTimes,
} from "react-icons/fa";
import { CANDY_VERSIONS, getCandyConfig, getCandyIdByName } from "../../data/candyConfig";
import { getCandyBaseLogo, getCandyLogo } from "../../data/candyLogos";
import { getRewardAnimation } from "./animations";
import "./LootCapsule.css";

/**
 * LootCapsule - Animated gacha capsule that reveals rewards
 * 
 * Animation Flow:
 * 1. Capsule drops and bounces (0-1.5s)
 * 2. Capsule opens (1.5-2.2s)
 * 3. Candy animation plays prominently (2.2-4.2s)
 * 4. Reward details fade in (4.2s+)
 * 5. Continue button appears
 */
const LootCapsule = ({
  reward = null,
  isVisible = false,
  onClose,
  onAnimationComplete,
}) => {
  // Phase: idle -> dropping -> bouncing -> opening -> animating -> revealed
  const [phase, setPhase] = useState("idle");
  const [showParticles, setShowParticles] = useState(false);
  const [showRewardDetails, setShowRewardDetails] = useState(false);

  // Get candy-specific animation
  const { Component: CandyAnimation, intensity, candyType } = getRewardAnimation(reward);

  // Stable noop callback - prevents animation useEffects from re-triggering
  // when LootCapsule re-renders (inline () => {} creates new refs each render)
  const noopCallback = useCallback(() => { }, []);

  // Animation sequence with proper timing
  useEffect(() => {
    if (isVisible && reward !== undefined) {
      // Reset states
      setPhase("dropping");
      setShowParticles(false);
      setShowRewardDetails(false);

      const timers = [];

      // Phase 1: Drop (0-800ms)
      timers.push(setTimeout(() => setPhase("bouncing"), 800));

      // Phase 2: Bounce (800-1500ms)
      timers.push(setTimeout(() => setPhase("opening"), 1500));

      // Phase 3: Open capsule, start candy animation (1500-2200ms)
      timers.push(setTimeout(() => {
        setPhase("animating");
        if (reward) {
          setShowParticles(true);
        }
      }, 2200));

      // Phase 4: Show reward details after candy animation (4200ms)
      // Candy animations run ~2 seconds
      timers.push(setTimeout(() => {
        setShowRewardDetails(true);
      }, 4200));

      // Phase 5: Full reveal with continue button (4500ms)
      timers.push(setTimeout(() => {
        setPhase("revealed");
        if (onAnimationComplete) {
          onAnimationComplete();
        }
      }, 4500));

      return () => timers.forEach(clearTimeout);
    } else {
      setPhase("idle");
      setShowParticles(false);
      setShowRewardDetails(false);
    }
  }, [isVisible, reward, onAnimationComplete]);

  // Get version styling
  const getVersionConfig = useCallback((versionOrRarity) => {
    const rarityToVersion = {
      common: 'Street',
      rare: 'Juiced',
      epic: 'Cracked',
      legendary: 'GoldMember',
    };

    const version = CANDY_VERSIONS[versionOrRarity]
      ? versionOrRarity
      : rarityToVersion[versionOrRarity?.toLowerCase()] || 'Street';

    const versionConfig = CANDY_VERSIONS[version] || CANDY_VERSIONS.Street;

    const tierIcons = {
      1: <FaStar />,
      2: <FaStar />,
      3: <FaBolt />,
      4: <FaBolt />,
      5: <FaCrown />,
      6: <FaCrown />,
    };

    return {
      ...versionConfig,
      versionKey: version,
      icon: tierIcons[versionConfig.tier] || <FaStar />,
    };
  }, []);

  // Generate background particles
  const renderParticles = () => {
    if (!showParticles || !reward) return null;

    const versionField = reward.version || reward.rarity;
    const versionConfig = getVersionConfig(versionField);
    const tier = versionConfig.tier || 1;
    const particleCount = tier >= 5 ? 24 : tier >= 3 ? 18 : 12;

    return (
      <div className="particles-container">
        {Array.from({ length: particleCount }).map((_, i) => (
          <div
            key={i}
            className={`particle particle-tier-${tier}`}
            style={{
              "--delay": `${Math.random() * 0.5}s`,
              "--angle": `${Math.random() * 360}deg`,
              "--distance": `${80 + Math.random() * 120}px`,
              "--size": `${4 + Math.random() * 8}px`,
              backgroundColor: versionConfig.color,
            }}
          />
        ))}
      </div>
    );
  };

  if (!isVisible) return null;

  const versionField = reward ? (reward.version || reward.rarity) : null;
  const versionConfig = reward ? getVersionConfig(versionField) : null;
  const tierClass = versionConfig ? `tier-${versionConfig.tier}` : "empty";
  const rewardCandyId = reward ? getCandyIdByName(reward.name) : null;
  const rewardCandy = rewardCandyId ? getCandyConfig(rewardCandyId) : null;
  const rewardLogo = rewardCandyId
    ? (versionConfig?.versionKey
      ? getCandyLogo(rewardCandyId, versionConfig.versionKey)
      : getCandyBaseLogo(rewardCandyId))
    : null;

  // Determine if capsule should be hidden (after opening)
  const hideCapsule = phase === "animating" || phase === "revealed";

  return (
    <div className={`loot-capsule-overlay ${phase}`}>
      {/* Background blur */}
      <div
        className="capsule-backdrop"
        onClick={phase === "revealed" ? onClose : undefined}
      />

      {/* Main container */}
      <div className="capsule-container">
        {/* Background particles */}
        {renderParticles()}

        {/* The capsule - hides after opening */}
        <div className={`capsule ${phase} ${tierClass} ${hideCapsule ? 'hidden' : ''}`}>
          <div className="capsule-top">
            <div className="shine" />
            <div className="capsule-ridge" />
          </div>
          <div className="capsule-bottom">
            <div className="shine" />
          </div>
          <div className="inner-glow" />
        </div>

        {/* Candy-specific animation - PROMINENT and centered */}
        {(phase === "animating" || phase === "revealed") && CandyAnimation && (
          <div className={`candy-animation-wrapper ${tierClass}`}>
            <CandyAnimation
              intensity={intensity}
              onComplete={noopCallback}
            />
          </div>
        )}

        {/* Reward details - horizontal bottom bar layout */}
        {(phase === "animating" || phase === "revealed") && (
          <>
            {/* Separator bar */}
            {showRewardDetails && <div className="reward-separator" />}

            <div className={`reward-reveal ${tierClass} ${showRewardDetails ? 'visible' : ''}`}>
              {reward ? (
                <>
                  {/* Center: Info card with name, rarity, amount */}
                  <div className="reward-info-card centered">
                    <div className="reward-name">
                      <span className={`reward-candy-logo ${rewardLogo ? '' : 'fallback'}`}>
                        {rewardLogo ? (
                          <img src={rewardLogo} alt={`${reward.name} logo`} loading="lazy" />
                        ) : (
                          <span className="reward-candy-icon">{rewardCandy?.icon || 'C'}</span>
                        )}
                      </span>
                      <span className="reward-name-text">{reward.name}</span>
                    </div>
                    <div className="reward-rarity" style={{ color: versionConfig?.color }}>
                      {versionConfig?.icon}
                      <span>{versionConfig?.label}</span>
                      {versionConfig?.icon}
                    </div>
                    <div className="reward-amount">x{reward.amount}</div>
                  </div>

                  {/* Continue button below the card (only when revealed) */}
                  {phase === "revealed" && (
                    <button className="close-btn centered" onClick={onClose}>
                      <span>CONTINUE</span>
                      <FaTimes />
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* No reward - centered layout */}
                  <div className="reward-info-card no-reward centered">
                    <div className="no-reward-text">Dry Pull</div>
                    <div className="no-reward-subtext">Better luck next time!</div>
                  </div>
                  {phase === "revealed" && (
                    <button className="close-btn centered" onClick={onClose}>
                      <span>CONTINUE</span>
                      <FaTimes />
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* Phase indicator during capsule animation */}
        {phase !== "animating" && phase !== "revealed" && (
          <div className="phase-text">
            {phase === "dropping" && "INCOMING..."}
            {phase === "bouncing" && "PREPARING..."}
            {phase === "opening" && "OPENING..."}
          </div>
        )}
      </div>
    </div>
  );
};

export default LootCapsule;
